import { useMemo, useState } from "react";
import { ArrowRight, GitBranch, Radar, TrendingUp } from "lucide-react";
import { Sankey, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PLATFORM_CHART_COLORS, formatNumber, getPlatformName } from "../constants";

const COPY = {
  ko: {
    graphTitle: "Network Graph",
    graphDesc: "왼쪽은 source, 오른쪽은 target 입니다. 선을 클릭하면 상세 값을 확인할 수 있습니다.",
    sankeyTitle: "Sankey Flow",
    sankeyDesc: "흐름의 두께는 선택한 지표의 크기를 반영합니다.",
    topFlows: "상위 흐름",
    selectedFlow: "선택된 흐름",
    events: "이벤트",
    avgLag: "평균 반응 시차",
    positiveRatio: "긍정 비율",
    adjustedLift: "보정 Lift",
    observedDelta: "관측 변화",
    noFlows: "현재 조건에서 시각화할 흐름이 없습니다.",
    noFlowsDesc: "필터를 완화하거나 기간을 변경해 보세요.",
    days: "일",
    mappedFlows: "매핑된 흐름",
  },
  en: {
    graphTitle: "Network Graph",
    graphDesc: "Sources are on the left, targets on the right. Click a line to inspect the flow.",
    sankeyTitle: "Sankey Flow",
    sankeyDesc: "Flow thickness follows the selected metric.",
    topFlows: "Top flows",
    selectedFlow: "Selected flow",
    events: "Events",
    avgLag: "Avg lag",
    positiveRatio: "Positive ratio",
    adjustedLift: "Adjusted Lift",
    observedDelta: "Observed Delta",
    noFlows: "No flows match the current filters.",
    noFlowsDesc: "Relax the filters or change the time window.",
    days: "d",
    mappedFlows: "Mapped flows",
  },
};

const getMetricValue = (item, mode) => {
  if (!item) return 0;
  if (mode === "adjusted") return item.total_adjusted_lift ?? 0;
  return item.total_follower_delta ?? 0;
};

const getMetricLabel = (mode, copy) => (mode === "adjusted" ? copy.adjustedLift : copy.observedDelta);

const getStrokeColor = (value) => {
  if (value > 0) return "#10b981";
  if (value < 0) return "#f43f5e";
  return "#94a3b8";
};

const GraphTooltipCard = ({ edge, copy, lang, metricMode }) => {
  if (!edge) return null;
  return (
    <Card data-testid="spillover-network-selected-flow-card" className="border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">{copy.selectedFlow}</CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" data-testid="spillover-network-selected-source">{getPlatformName(edge.source_platform, lang)}</Badge>
          <ArrowRight className="h-3.5 w-3.5" />
          <Badge variant="outline" data-testid="spillover-network-selected-target">{getPlatformName(edge.target_platform, lang)}</Badge>
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{getMetricLabel(metricMode, copy)}</p>
          <p className="mt-1 text-lg font-semibold" style={{ color: getStrokeColor(edge.primary_value) }}>{edge.primary_value > 0 ? "+" : ""}{formatNumber(edge.primary_value)}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{copy.observedDelta}</p>
          <p className="mt-1 text-lg font-semibold" style={{ color: getStrokeColor(edge.total_follower_delta) }}>{edge.total_follower_delta > 0 ? "+" : ""}{formatNumber(edge.total_follower_delta)}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{copy.events}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{edge.event_count}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{copy.positiveRatio}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{edge.positive_ratio}%</p>
        </div>
      </CardContent>
    </Card>
  );
};

const SankeyTooltip = ({ active, payload, lang, copy, metricMode }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  const isLink = typeof item.source === "object" && typeof item.target === "object";
  return (
    <div className="rounded-2xl border border-border/70 bg-background/95 p-3 text-xs shadow-xl">
      {isLink ? (
        <>
          <p className="font-semibold text-foreground">{getPlatformName(item.source.name, lang)} → {getPlatformName(item.target.name, lang)}</p>
          <p className="mt-1 text-muted-foreground">{getMetricLabel(metricMode, copy)}: {formatNumber(item.value)}</p>
        </>
      ) : (
        <>
          <p className="font-semibold text-foreground">{getPlatformName(item.name, lang)}</p>
          <p className="mt-1 text-muted-foreground">{copy.mappedFlows}: {formatNumber(item.value || 0)}</p>
        </>
      )}
    </div>
  );
};

export const SpilloverNetworkVisualization = ({
  analysis,
  lang = "ko",
  loading = false,
  snapshotRef,
  viewMode = "graph",
  metricMode = "adjusted",
  confidenceFilter = "all",
}) => {
  const copy = COPY[lang] || COPY.ko;
  const [selectedEdgeId, setSelectedEdgeId] = useState("");

  const edges = useMemo(() => {
    const pairs = analysis?.pair_summary || [];
    return pairs
      .map((pair) => ({ ...pair, primary_value: getMetricValue(pair, metricMode) }))
      .filter((pair) => pair.source_platform !== pair.target_platform)
      .filter((pair) => Math.abs(pair.primary_value) > 0)
      .filter((pair) => confidenceFilter === "all" || pair.confidence === confidenceFilter)
      .sort((left, right) => Math.abs(right.primary_value) - Math.abs(left.primary_value));
  }, [analysis?.pair_summary, confidenceFilter, metricMode]);

  const sources = useMemo(() => [...new Set(edges.map((e) => e.source_platform))], [edges]);
  const targets = useMemo(() => [...new Set(edges.map((e) => e.target_platform))], [edges]);
  const strongestFlow = edges[0];
  const selectedEdge = edges.find((e) => `${e.source_platform}-${e.target_platform}` === selectedEdgeId) || strongestFlow;

  const graphLayout = useMemo(() => {
    const width = 980;
    const height = Math.max(340, Math.max(sources.length, targets.length) * 78 + 70);
    const sourceNodes = sources.map((platform, index) => ({
      platform,
      x: 170,
      y: 60 + index * ((height - 120) / Math.max(sources.length - 1, 1)),
    }));
    const targetNodes = targets.map((platform, index) => ({
      platform,
      x: width - 170,
      y: 60 + index * ((height - 120) / Math.max(targets.length - 1, 1)),
    }));
    const edgeMax = Math.max(...edges.map((e) => Math.abs(e.primary_value)), 1);
    return {
      width,
      height,
      sourceNodes,
      targetNodes,
      edgePaths: edges.map((edge) => {
        const sourceNode = sourceNodes.find((n) => n.platform === edge.source_platform);
        const targetNode = targetNodes.find((n) => n.platform === edge.target_platform);
        const strokeWidth = 2 + (Math.abs(edge.primary_value) / edgeMax) * 13;
        return {
          ...edge,
          id: `${edge.source_platform}-${edge.target_platform}`,
          path: `M ${sourceNode?.x ?? 0} ${sourceNode?.y ?? 0} C 380 ${sourceNode?.y ?? 0}, 600 ${targetNode?.y ?? 0}, ${targetNode?.x ?? 0} ${targetNode?.y ?? 0}`,
          strokeWidth,
          sourceNode,
          targetNode,
        };
      }),
    };
  }, [edges, sources, targets]);

  const sankeyData = useMemo(() => {
    const nodeNames = [...new Set(edges.flatMap((e) => [e.source_platform, e.target_platform]))];
    return {
      nodes: nodeNames.map((name, index) => ({ name, index, value: 0 })),
      links: edges.map((e) => ({
        source: nodeNames.indexOf(e.source_platform),
        target: nodeNames.indexOf(e.target_platform),
        value: Math.max(Math.abs(e.primary_value), 1),
      })),
    };
  }, [edges]);

  if (loading) return <Skeleton className="h-[500px] w-full rounded-[28px]" />;

  if (!edges.length) {
    return (
      <Card data-testid="spillover-network-empty-state" className="border-border/60">
        <CardContent className="py-12 text-center">
          <Radar className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{copy.noFlows}</p>
          <p className="mt-1 text-xs text-muted-foreground/60">{copy.noFlowsDesc}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={snapshotRef} className="space-y-4" data-testid="spillover-network-visualization-root">
      {viewMode === "graph" && (
        <>
          <Card data-testid="spillover-network-graph-card" className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <GitBranch className="h-4 w-4 text-primary" /> {copy.graphTitle}
              </CardTitle>
              <CardDescription>{copy.graphDesc}</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <svg viewBox={`0 0 ${graphLayout.width} ${graphLayout.height}`} className="min-w-[820px] w-full" data-testid="spillover-network-graph-svg">
                {graphLayout.edgePaths.map((edge) => (
                  <path
                    key={edge.id}
                    d={edge.path}
                    fill="none"
                    stroke={getStrokeColor(edge.primary_value)}
                    strokeWidth={edge.strokeWidth}
                    strokeOpacity={selectedEdge?.id === edge.id ? 0.95 : 0.45}
                    strokeLinecap="round"
                    onClick={() => setSelectedEdgeId(edge.id)}
                    className="cursor-pointer transition-opacity"
                    data-testid={`spillover-network-edge-${edge.source_platform}-${edge.target_platform}`}
                  />
                ))}
                {graphLayout.sourceNodes.map((node) => (
                  <g key={`source-${node.platform}`} data-testid={`spillover-network-source-node-${node.platform}`}>
                    <circle cx={node.x} cy={node.y} r="18" fill={PLATFORM_CHART_COLORS[node.platform] || "#64748b"} opacity="0.18" />
                    <circle cx={node.x} cy={node.y} r="10" fill={PLATFORM_CHART_COLORS[node.platform] || "#64748b"} />
                    <text x={node.x - 26} y={node.y - 18} textAnchor="end" className="fill-foreground text-[13px] font-medium">{getPlatformName(node.platform, lang)}</text>
                  </g>
                ))}
                {graphLayout.targetNodes.map((node) => (
                  <g key={`target-${node.platform}`} data-testid={`spillover-network-target-node-${node.platform}`}>
                    <circle cx={node.x} cy={node.y} r="18" fill={PLATFORM_CHART_COLORS[node.platform] || "#64748b"} opacity="0.18" />
                    <circle cx={node.x} cy={node.y} r="10" fill={PLATFORM_CHART_COLORS[node.platform] || "#64748b"} />
                    <text x={node.x + 26} y={node.y - 18} textAnchor="start" className="fill-foreground text-[13px] font-medium">{getPlatformName(node.platform, lang)}</text>
                  </g>
                ))}
              </svg>
            </CardContent>
          </Card>
          <GraphTooltipCard edge={selectedEdge} copy={copy} lang={lang} metricMode={metricMode} />
        </>
      )}

      {viewMode === "sankey" && (
        <div className="grid gap-4 xl:grid-cols-[1.25fr_0.9fr]">
          <Card data-testid="spillover-network-sankey-card" className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <TrendingUp className="h-4 w-4 text-primary" /> {copy.sankeyTitle}
              </CardTitle>
              <CardDescription>{copy.sankeyDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <Sankey data={sankeyData} nodePadding={34} nodeWidth={18} iterations={48} nameKey="name">
                    <RechartsTooltip content={<SankeyTooltip lang={lang} copy={copy} metricMode={metricMode} />} />
                  </Sankey>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="spillover-network-top-flows-card" className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">{copy.topFlows}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {edges.slice(0, 6).map((edge, index) => (
                <button
                  key={`${edge.source_platform}-${edge.target_platform}`}
                  type="button"
                  onClick={() => setSelectedEdgeId(`${edge.source_platform}-${edge.target_platform}`)}
                  className="w-full rounded-2xl border border-border/60 bg-background/70 p-3 text-left transition-colors hover:border-primary/30"
                  data-testid={`spillover-network-top-flow-${index}`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Badge variant="outline">{getPlatformName(edge.source_platform, lang)}</Badge>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <Badge variant="outline">{getPlatformName(edge.target_platform, lang)}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span style={{ color: getStrokeColor(edge.primary_value) }}>{edge.primary_value > 0 ? "+" : ""}{formatNumber(edge.primary_value)} {getMetricLabel(metricMode, copy)}</span>
                    <span>{copy.events} {edge.event_count}</span>
                    <span>{edge.confidence}</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
